import React, { useState, useEffect } from 'react'
import { UnityEcosystemBar } from '@unity-cosmos/chrome'
import { TopNav } from './components/TopNav.jsx'
import { HyperfocusCard } from './components/HyperfocusCard.jsx'
import { DopamineShieldCard } from './components/DopamineShieldCard.jsx'
import { SynapseDuelCard } from './components/SynapseDuelCard.jsx'
import { GameHub } from './components/GameHub.jsx'
import { DailyMissions } from './components/DailyMissions.jsx'
import { LiveSquadFeed } from './components/LiveSquadFeed.jsx'
import { BottomNav } from './components/BottomNav.jsx'

// Playable Games
import { NeuroMathGame } from './components/games/NeuroMathGame.jsx'
import { FakeNewsGame } from './components/games/FakeNewsGame.jsx'
import { SynapseBattleGame } from './components/games/SynapseBattleGame.jsx'
import { ControlDockSlidePuzzle } from './components/games/ControlDockSlidePuzzle.jsx'

// Selected skill games (local-first)
import { DetectiveGame } from './games/detective/DetectiveGame.jsx'
import { EcoGame } from './games/eco/EcoGame.jsx'
import { DueloGame } from './games/duelo/DueloGame.jsx'
import { RafagaGame } from './games/rafaga/RafagaGame.jsx'
import { RefraneroGame } from './games/refranero/RefraneroGame.jsx'
import { CronicasGame } from './games/cronicas/CronicasGame.jsx'
import { BazarGame } from './games/bazar/BazarGame.jsx'
import { MelodiasGame } from './games/melodias/MelodiasGame.jsx'
import { PulsoGame, SombraGame, FiltroGame, AnclaGame, CierreGame } from './games/orbita/OrbitGames.jsx'
const AjedrezGame = React.lazy(() => import('./games/ajedrez/AjedrezGame.jsx').then((m) => ({ default: m.AjedrezGame })))
import { CosmosStage } from './components/CosmosStage.jsx'

const SKILL_GAMES = {
  rafaga: RafagaGame,
  refranero: RefraneroGame,
  cronicas: CronicasGame,
  bazar: BazarGame,
  melodias: MelodiasGame,
  pulso: PulsoGame,
  sombra: SombraGame,
  filtro: FiltroGame,
  ancla: AnclaGame,
  cierre: CierreGame,
  ajedrez: AjedrezGame,
}

// Views
import { RankingView } from './components/views/RankingView.jsx'
import { ProfileView } from './components/views/ProfileView.jsx'

import {
  bootUnityHandoff,
  isUnityEmbedded,
  readSharedUnitySession,
} from './lib/unityAuth.js'
import { load, awardBp } from './games/lib/store.js'
import { tr, useLang } from './games/lib/lang.js'
import { fetchRank, publishScore } from './lib/ranking.js'

const SAVES_OWN_SCORE = new Set(['detective', 'eco', 'melodias', 'duelo', 'cronicas', 'bazar', 'refranero', 'rafaga'])

export default function App() {
  const [lang] = useLang()
  const [activeTab, setActiveTab] = useState('train')
  const [currentGame, setCurrentGame] = useState(null)
  const [streak, setStreak] = useState(() => load().streak || 0)
  const [bp, setBp] = useState(() => load().bp || 0)
  const [rank, setRank] = useState(null)
  const [minutesSaved, setMinutesSaved] = useState(35)
  const [session, setSession] = useState(() => readSharedUnitySession())
  const [embedded, setEmbedded] = useState(() => isUnityEmbedded())

  const syncStanding = async (nextSession = session) => {
    const saved = load()
    const localScore = saved.bp || 0
    setBp(localScore)
    setStreak(saved.streak || 0)
    try {
      const data = localScore > 0
        ? await publishScore(nextSession, localScore)
        : await fetchRank(nextSession)
      setBp(Math.max(localScore, data.score || 0))
      setRank(data.rank || null)
    } catch {
      setRank(null)
    }
  }

  useEffect(() => {
    setEmbedded(isUnityEmbedded())
    return bootUnityHandoff({
      onSessionChange: (next) => setSession(next),
    })
  }, [])

  useEffect(() => {
    syncStanding(session)
    // The standing follows the signed-in account.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  const handleClaimReward = (addedBp) => {
    const amount = Math.max(0, Math.round(Number(addedBp) || 0))
    if (amount) awardBp(amount)
    setMinutesSaved((prev) => prev + 5)
    syncStanding()
  }

  const handleGameComplete = (earnedBp) => {
    if (!SAVES_OWN_SCORE.has(currentGame)) {
      const amount = Math.max(0, Math.round(Number(earnedBp) || 0))
      if (amount) awardBp(amount)
    }
    setMinutesSaved((prev) => prev + 5)
    syncStanding()
  }

  const launchGame = (id) => {
    if (id === 'critical' || id === 'lexicon') setCurrentGame('duel')
    else setCurrentGame(id)
  }

  const back = () => {
    syncStanding()
    setCurrentGame(null)
  }
  const done = (score) => handleGameComplete(score)
  const frame = (node, accent, wide) => (
    <div className="cosmos-play">
      <CosmosStage accent={accent} wide={wide}>{node}</CosmosStage>
    </div>
  )

  if (currentGame === 'math') {
    return frame(<NeuroMathGame onBack={back} onComplete={done} />, '#38bdf8')
  }
  if (currentGame === 'fakenews') {
    return frame(<FakeNewsGame onBack={back} onComplete={done} />, '#f97316')
  }
  if (currentGame === 'dockpuzzle') {
    return frame(<ControlDockSlidePuzzle onBack={back} onComplete={done} />, '#f472b6')
  }
  if (currentGame === 'duel') {
    return frame(<SynapseBattleGame onBack={back} onComplete={done} session={session} />, '#a78bfa')
  }
  if (currentGame === 'detective') {
    return frame(<DetectiveGame onBack={back} onComplete={done} />, '#22d3ee')
  }
  if (currentGame === 'eco') {
    return frame(<EcoGame onBack={back} onComplete={done} />, '#34d399')
  }
  if (currentGame === 'duelo') {
    return frame(<DueloGame onBack={back} onComplete={done} session={session} />, '#fbbf24')
  }

  const SkillGame = SKILL_GAMES[currentGame]
  if (SkillGame) {
    return frame(
      <React.Suspense fallback={<div className="cbp-game-loading">Cargando arena…</div>}>
        <SkillGame onBack={back} onComplete={done} />
      </React.Suspense>,
      currentGame === 'ajedrez' ? '#e7d3b0' : '#22d3ee',
      currentGame === 'ajedrez',
    )
  }


  return (
    <div className={`cbp-shell ${embedded ? 'cbp-shell--embedded' : ''} bg-[#0a0d14] text-white min-h-screen pb-20 md:pb-10`}>
      {/* Unity Cosmos Shared Ecosystem Bar */}
      <div key="uc-chrome-wrapper" className="w-full">
        {!embedded ? (
          <UnityEcosystemBar currentId="brainpower" lang={lang} showAppsMenu showLogout={false} />
        ) : null}
      </div>

      {/* Top Status & Desktop Nav */}
      <TopNav
        score={bp}
        rank={rank}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab)}
      />

      {/* Main Container — responsive grid for laptop/desktop */}
      <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
        {/* Main Training Tab */}
        {activeTab === 'train' && (
          <div className="animate-fade-in">
            <GameHub onSelectModule={launchGame} />
          </div>
        )}

        {/* Missions Tab */}
        {activeTab === 'missions' && (
          <div className="max-w-3xl mx-auto space-y-5 animate-fade-in pt-2">
            <h2 className="text-xl sm:text-2xl font-black text-white">{tr(lang, 'Misiones y desafíos', 'Missions and challenges')}</h2>
            <DailyMissions onClaimReward={handleClaimReward} />
            <HyperfocusCard streakDays={streak} />
            <DopamineShieldCard minutesSaved={minutesSaved} bpGenerated={bp} />
          </div>
        )}

        {/* Duels Tab */}
        {activeTab === 'duels' && (
          <div className="max-w-3xl mx-auto space-y-5 animate-fade-in pt-2">
            <h2 className="text-xl sm:text-2xl font-black text-white">{tr(lang, 'Arena de duelos 1v1', '1v1 duel arena')}</h2>
            <SynapseDuelCard onLaunchDuel={() => setCurrentGame('duel')} session={session} />
            <LiveSquadFeed onDuelChallenge={() => setCurrentGame('duel')} />
          </div>
        )}

        {/* Ranking Tab */}
        {activeTab === 'ranking' && (
          <div className="max-w-4xl mx-auto animate-fade-in">
            <RankingView session={session} onBack={() => setActiveTab('train')} />
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-3xl mx-auto animate-fade-in">
            <ProfileView session={session} bp={bp} rank={rank} streak={streak} />
          </div>
        )}
      </main>

      {/* Bottom Sticky Navigation (Mobile only) */}
      <BottomNav activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab)} />
    </div>
  )
}
